// @ts-check
import { Trans } from '@lingui/macro';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import {
  Box,
  Button,
  CloseIcon,
  Flex,
  Link,
  SuccessIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';
import FilterColumns, { useFilterColumns } from './FilterColumns';
const EMPTY = '(empty)';
// List of "Import Column Name" that should not be mapped.
const excludeSourceFields = ['product.instanceId'];

/**
 * Renders the table to map the fields
 * @param {Object} props
 * @param {Object} props.bottlebooksFieldsMap - the mapped fields
 * @param {Object[]} props.bottlebooksFieldsSource - the bottlebooks fields source
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFields - the options to be rendered on the select boxes
 * @param {function} props.onChange - callback when one field is mapped
 */
export default function FieldsTable({
  bottlebooksFieldsMap,
  bottlebooksFieldsSource,
  sourceFields,
  onChange,
}) {
  sourceFields = sourceFields.map((sourceField, index) => ({
    ...sourceField,
    index,
  }));

  const filterProps = useFilterColumns();
  const withHeader = sourceFields?.filter((sourceField) => sourceField.name);
  const withoutHeader = sourceFields?.filter(
    (sourceField) => !sourceField.name
  );

  return (
    <React.Fragment>
      <Box>
        <FilterColumns {...filterProps} sx={{ float: 'left' }} />
      </Box>
      <Table
        sx={{
          border: '1px solid',
          borderColor: 'brand.lightGray',
          width: '100%',
        }}
        // {...rest}
      >
        <TableHead>
          <MapFieldHeaderRow />
        </TableHead>
        <TableBody>
          {withHeader?.map((sourceField, i) => (
            <MapFieldRow
              key={`${sourceField.name}_${i}`}
              index={sourceField.index}
              sourceField={sourceField}
              bottlebooksFieldsSource={bottlebooksFieldsSource}
              bottlebooksFieldsMap={bottlebooksFieldsMap}
              onChange={onChange}
              showingColumns={filterProps.showingColumns}
            />
          ))}
          {withoutHeader?.map((sourceField, i) => (
            <MapFieldRow
              key={`${sourceField.name}_${i}`}
              index={sourceField.index}
              sourceField={sourceField}
              bottlebooksFieldsSource={bottlebooksFieldsSource}
              bottlebooksFieldsMap={bottlebooksFieldsMap}
              onChange={onChange}
              showingColumns={filterProps.showingColumns}
            />
          ))}
        </TableBody>
      </Table>
    </React.Fragment>
  );
}

function MapFieldHeaderRow() {
  return (
    <TableRow>
      <TableHeader
        sx={{
          backgroundColor: 'brand.lightGray',
          textAlign: 'center',
          width: '10%',
        }}
      >
        <Text variant="smallest">
          <Trans>Matched</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: '30%' }}>
        <Text variant="smallest">
          <Trans>Import column name</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: '40%' }}>
        <Text variant="smallest">
          <Trans>Bottlebooks field name</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: '20%' }}>
        <Text variant="smallest">
          <Trans>Data preview</Trans>
        </Text>
      </TableHeader>
    </TableRow>
  );
}

/**
 * @param {object} props
 * @param {number} props.index
 * @param {{name: string, sampleRows: string[]}} props.sourceField
 * @param {Object[]} props.bottlebooksFieldsSource - the bottlebooks fields source
 * @param {Object} props.bottlebooksFieldsMap
 * @param {function} props.onChange
 * @param { 'showAll' | 'showMatchedOnly' | 'showUnmatchedOnly' } props.showingColumns
 * @returns
 */
function MapFieldRow({
  index,
  sourceField,
  bottlebooksFieldsSource,
  bottlebooksFieldsMap,
  onChange,
  showingColumns,
}) {
  const name = sourceField.name || colToLetter(index + 1);
  // Exclude columns from the excel file that most likely shouldn't be mapped.
  if (excludeSourceFields.includes(name)) return null;
  const emptyColl = !sourceField.name && !sourceField?.sampleRows?.length;
  // If sampleRows is without values it means column is empty and we can skip row rendering.
  // Should we use separate resolver to determine this? e.g. field.hasValues ?
  const isMapped =
    bottlebooksFieldsMap?.[sourceField?.name]?.bottlebooksFieldName;

  if (showingColumns === 'showMatchedOnly' && !isMapped) return null;
  if (showingColumns === 'showUnmatchedOnly' && isMapped) return null;

  return emptyColl ? null : (
    <TableRow>
      <TableCell sx={{ height: '90px', textAlign: 'center' }}>
        {isMapped && <SuccessIcon sx={{ color: 'brand.successGreen' }} />}
      </TableCell>
      <TableCell
        sx={{ height: '90px', textAlign: 'left', position: 'relative' }}
      >
        {/* <Text variant="default">{column.label}</Text> */}
        <Text variant="default">{name}</Text>
      </TableCell>
      <TableCell sx={{ height: '90px', textAlign: 'left' }}>
        {sourceField.name ? (
          <NotRequiredOption
            bottlebooksFieldsSource={bottlebooksFieldsSource}
            sourceField={sourceField}
            bottlebooksFieldsMap={bottlebooksFieldsMap}
            onChange={(value) => {
              onChange(value?.key, sourceField);
            }}
          />
        ) : (
          <Text variant="smallest" sx={{ color: 'brand.warningRed' }}>
            <Trans>The column name is required for mapping.</Trans>
          </Text>
        )}
      </TableCell>
      <TableCell sx={{ height: '90px', textAlign: 'left' }}>
        <SampleRows sampleRows={sourceField.sampleRows} />
      </TableCell>
    </TableRow>
  );
}

/**
 * Renders 3 samples for a given source field
 * @param {object} props
 * @param {string[]} props.sampleRows
 * @returns
 */
function SampleRows({ sampleRows }) {
  if (!sampleRows?.length) return null;

  const unique = sampleRows
    .reduce((acc, val) => {
      if (!acc.includes(val)) acc.push(val);
      return acc;
    }, [])
    .slice(0, 3);
  return (
    <Box>
      {unique.map((value, index) => (
        <Text
          key={index} // We need to cheat here because values aren't unique.
          sx={{ maxWidth: '280px' }}
          nowrap={1}
          variant="smallest"
        >
          {value}
        </Text>
      ))}
    </Box>
  );
}

function colToLetter(n) {
  return 'Column ' + _colToLetter(n);
  function _colToLetter(n) {
    const l = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return (
      '' +
      (n > 26
        ? _colToLetter(Math.floor(n / 26) - 1) + _colToLetter(n % 26)
        : l[n])
    );
  }
}

/**
 * Renders optional import column name
 * @param {object} props
 * @param {string[]} props.bottlebooksFieldsSource
 * @param {object} props.sourceField
 * @param {object[]} props.bottlebooksFieldsMap
 * @param {function} props.onChange
 * @returns
 */
function NotRequiredOption({
  bottlebooksFieldsSource,
  sourceField,
  bottlebooksFieldsMap,
  onChange,
}) {
  const [optionsVisible, setOptionsVisible] = React.useState(
    !!bottlebooksFieldsMap?.[sourceField?.name]?.bottlebooksFieldName
  ); //sourceField?.name);
  //selected option

  if (optionsVisible) {
    return (
      <Flex>
        <Box sx={{ width: '90%' }}>
          <Options
            bottlebooksFieldsSource={bottlebooksFieldsSource}
            bottlebooksFieldName={
              bottlebooksFieldsMap?.[sourceField?.name]?.bottlebooksFieldName
            }
            onChange={onChange}
          />
        </Box>
        <Flex sx={{ width: '10%', alignItems: 'center' }}>
          <Button
            variant="text"
            sx={{ marginLeft: 2, lineHeight: '17px' }}
            onClick={() => {
              setOptionsVisible(false);
              onChange(EMPTY);
            }}
          >
            <CloseIcon size="xxsmall" />
          </Button>
        </Flex>
      </Flex>
    );
  }
  return <Link onClick={() => setOptionsVisible(true)}>Map...</Link>;
}

function Options({ bottlebooksFieldsSource, bottlebooksFieldName, onChange }) {
  return (
    <Autocomplete
      sx={{ width: '100%' }}
      options={bottlebooksFieldsSource.filter(Boolean)}
      disablePortal
      getOptionLabel={(field) => {
        const fieldSource = getFieldOption({ field, bottlebooksFieldsSource });
        return fieldSource?.required
          ? `${fieldSource?.label} (*)`
          : fieldSource?.label;
      }}
      getOptionSelected={(field, v) => {
        const fieldSource = getFieldOption({ field, bottlebooksFieldsSource });
        return fieldSource?.key;
      }}
      value={bottlebooksFieldName}
      onChange={(e, value) => {
        onChange(value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name="selectBBFieldName"
          label=""
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
}

/**
 * Used to get the bottebooks field source config related with the given field
 * @param {object} props
 * @param {object|string} props.field
 * @param {object[]} props.bottlebooksFieldsSource
 * @returns
 */
function getFieldOption({ field, bottlebooksFieldsSource }) {
  //We just have the first position of bottlebooksFields (.0.) in options, so we have to pick '.0.' when the field index is higher than 0.
  if (typeof field === 'string') {
    field = field.replace(/\.\d+\./, '.0.');
  } else {
    field.key = field?.key?.replace(/\.\d+\./, '.0.');
  }
  return bottlebooksFieldsSource.find(
    ({ key }) => key === field || key === field?.key
  );
}
